import React, {
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { TooltipContext } from '@providers/TooltipProvider';
import { isShareEnabled, navigatorShare } from '@utils/navigatorShare';
import { getShopDetailsInfoRoute } from '@services/routes.service';

import { POINTER_POSITION } from '@components/Tooltip';
import Heart from '@components/Heart';
import ShareAndroid from '@pedidosya/web-fenix/icons/ShareAndroid';
import Information from '@pedidosya/web-fenix/icons/Information';

import { ONLINE_PAYMENT_TOOLTIP_TIMER } from '@shopDetails/constants';

import { onKeyUpFunction, onKeyDownFunction } from '@utils/a11y';
import OnlinePaymentAlert from './components/OnlinePaymentAlert';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  z-index: 1000;
`;

const InfoRouteContainer = styled.div`
  position: relative;
`;

const InfoRouteAlert = styled.div`
  position: absolute;
  top: 10px;
  left: 26px;
  width: 7px;
  height: 7px;
  border: solid 1px ${({ theme }) => theme.colors.white};
  border-radius: 100%;
  background-color: ${({ theme }) => theme.commons.tooltip.background};
`;

const Icon = styled.div`
  margin: 10px;
  ${props =>
    props.backgroudIcon &&
    css`
      background-color: ${({ theme }) => theme.colors.platinum};
      border-radius: 100%;
      width: 24px;
      height: 24px;
      align-items: center;
      display: flex;
      justify-content: center;
      margin: 0px;
      margin-left: 10px;
    `};
`;

function ShopHeaderActions({
  shopId,
  shopName,
  shopIsFavorite,
  isUserLogged,
  showOnlinePaymentTooltip,
  backgroudIcon,
  match: {
    params: { city, restaurant },
  },
  onToggleFavorite,
  closeOnlinePaymentTooltip,
  size,
  className,
  trackShopInfoClicked,
  trackShareButtonClicked,
}) {
  const infoRouteLinkRef = useRef();
  const timeoutRef = useRef();
  const { setTooltip } = useContext(TooltipContext);

  const closeTooltip = useCallback(() => {
    closeOnlinePaymentTooltip();
    window.removeEventListener('scroll', closeTooltip);
    window.removeEventListener('touchstart', closeTooltip);
    clearTimeout(timeoutRef.current);
  }, [closeOnlinePaymentTooltip]);

  useEffect(() => {
    if (showOnlinePaymentTooltip) {
      setTooltip({
        content: <OnlinePaymentAlert />,
        target: infoRouteLinkRef.current,
        open: true,
        pointerPosition: POINTER_POSITION.TOP_RIGHT,
        styles: { top: '-14px', left: '3px' },
      });
      window.addEventListener('scroll', closeTooltip);
      window.addEventListener('touchstart', closeTooltip);
      timeoutRef.current = setTimeout(
        closeTooltip,
        ONLINE_PAYMENT_TOOLTIP_TIMER,
      );
    }

    return () => {
      setTooltip({ open: false });
      window.removeEventListener('scroll', closeTooltip);
      window.removeEventListener('touchstart', closeTooltip);
    };
  }, [
    showOnlinePaymentTooltip,
    closeOnlinePaymentTooltip,
    setTooltip,
    closeTooltip,
  ]);

  const shopInfoRoute = useMemo(
    () => getShopDetailsInfoRoute({ city, restaurant }),
    [city, restaurant],
  );

  const onFavoriteClick = useCallback(
    () => onToggleFavorite(shopId, !shopIsFavorite),
    [onToggleFavorite, shopIsFavorite, shopId],
  );

  const onShareClick = useCallback(() => {
    trackShareButtonClicked();
    navigatorShare({ title: shopName, url: window.location.href });
  }, [shopName, trackShareButtonClicked]);

  const trackShopInfoClick = useCallback(() => trackShopInfoClicked(), [
    trackShopInfoClicked,
  ]);

  return (
    <Container className={className}>
      {isUserLogged && (
        <Icon backgroudIcon={backgroudIcon}>
          <Heart
            isFavorite={shopIsFavorite}
            onClick={onFavoriteClick}
            onKeyUp={e => onKeyUpFunction(e, onFavoriteClick)}
            onKeyDown={e => onKeyDownFunction(e, onFavoriteClick)}
            size={size}
          />
        </Icon>
      )}

      {isShareEnabled() && (
        <Icon onClick={onShareClick} backgroudIcon={backgroudIcon}>
          <ShareAndroid aria-label="Compartir" size={size} />
        </Icon>
      )}

      <InfoRouteContainer>
        <div ref={infoRouteLinkRef}>
          <Link to={shopInfoRoute} onClick={trackShopInfoClick}>
            <Icon backgroudIcon={backgroudIcon}>
              <Information size={size} />
            </Icon>
          </Link>
        </div>
        {showOnlinePaymentTooltip && <InfoRouteAlert />}
      </InfoRouteContainer>
    </Container>
  );
}

ShopHeaderActions.propTypes = {
  shopId: PropTypes.number.isRequired,
  shopName: PropTypes.string.isRequired,
  shopIsFavorite: PropTypes.bool,
  isUserLogged: PropTypes.bool.isRequired,
  showOnlinePaymentTooltip: PropTypes.bool,
  match: PropTypes.shape({
    params: PropTypes.shape({
      city: PropTypes.string,
      restaurant: PropTypes.string,
    }),
  }).isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  closeOnlinePaymentTooltip: PropTypes.func.isRequired,
  backgroudIcon: PropTypes.bool,
  size: PropTypes.string,
  className: PropTypes.string,
  trackShopInfoClicked: PropTypes.func.isRequired,
  trackShareButtonClicked: PropTypes.func.isRequired,
};

ShopHeaderActions.defaultProps = {
  shopIsFavorite: false,
  showOnlinePaymentTooltip: true,
  backgroudIcon: false,
  size: 'medium',
};

export default ShopHeaderActions;
